import { useMutation, useQuery } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { Button } from '@optools/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@optools/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { LIFT_SLATE, SET_SLATE } from '@optools/services/operator-gateway/logger/mutations';
import { GET_SLATES } from '@optools/services/operator-gateway/logger/queries';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import {
  MediaFeedType,
  Slate,
  SlateType,
  Sport,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { getMediaId } from '@optools/utils/pipeline';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { getIsInAdBreak } from '../utils/ad-break';
import { determineNotificationPosition } from '../utils/notification';

const findBestSlate = (activeSlateCode: string | null | undefined, slates: Slate[]) => {
  const slate = slates.find((slate) => slate.code === activeSlateCode);
  return slate || slates[0];
};

const getUniqueAndSortedSlates = (slates: Slate[]) => {
  // Keep track of codes we've already seen
  const uniqueCodes = new Set<string>();

  // Filter the slates to only include ones with unique codes
  const uniqueSlates = slates.filter((slate) => {
    if (!slate.code) return false;
    if (uniqueCodes.has(slate.code)) return false;
    uniqueCodes.add(slate.code);
    return true;
  });

  // Sort Slates Alphabetically to maintain consistent order
  return uniqueSlates.toSorted((a, b) => a.code.localeCompare(b.code));
};

export interface SlateControlsProps {
  debugMode?: boolean;
  disabled?: boolean;
  pipeline: LoggerGamePipeline;
  side: MediaFeedType;
}

export function SlateControls({
  debugMode = false,
  disabled = false,
  pipeline,
  side,
}: SlateControlsProps) {
  const { activeSlateCode, adBreakDurationInSeconds, adBreakStartedDateTime, id } = pipeline;
  const mediaId = getMediaId(pipeline);

  const { data, dataState } = useQuery(GET_SLATES, {
    variables: { sport: Sport.MLB, type: SlateType.MID },
  });

  const slates = useMemo(() => {
    if (!data?.slates) return [];
    return getUniqueAndSortedSlates(data.slates);
  }, [data?.slates]);

  useEffect(() => {
    if (dataState === 'complete' && !data?.slates?.length) {
      toast.error('Failed to Get Slates', {
        description: 'Could not retrieve mid slate types',
        duration: Infinity,
        position: side === MediaFeedType.HOME ? 'top-right' : 'top-left',
      });
    }
  }, [data?.slates, dataState, side]);

  const [selectedSlate, setSelectedSlate] = useState<Slate>(() => {
    return findBestSlate(activeSlateCode, slates);
  });

  const [liftSlate, { loading: liftSlateStatusLoading }] = useMutation(LIFT_SLATE, {
    onCompleted: ({ liftSlate }) => {
      const { reason, success } = liftSlate[0];
      if (!success) {
        toast.error('Failed to Drop Slate', {
          description: reason ?? 'Reason: Unknown',
          duration: Infinity,
          position: determineNotificationPosition(side),
        });
      }
    },
  });

  const [setSlate, { loading: setSlateStatusLoading }] = useMutation(SET_SLATE, {
    onCompleted: ({ setSlate }) => {
      const { reason, success } = setSlate[0];
      if (!success) {
        toast.error('Failed to Insert Slate', {
          description: reason ?? 'Reason: Unknown',
          duration: Infinity,
          position: determineNotificationPosition(side),
        });
      }
    },
  });

  const slateIsActive = !!activeSlateCode && slates.some((slate) => slate.code === activeSlateCode);

  useEffect(() => {
    const bestSlate = findBestSlate(activeSlateCode, slates);
    setSelectedSlate(bestSlate);
  }, [activeSlateCode, slates]);

  const handleClickSelectSlate = (code: string) => {
    const slate = slates.find((item) => item.code === code);
    if (!slate) {
      return;
    }
    setSelectedSlate(slate);
  };

  const handleClickConfirmUpdate = () => {
    if (slateIsActive) {
      liftSlate({ variables: { mediaIds: [mediaId] } });
    } else {
      const { code: slateCode } = selectedSlate || {};
      setSlate({ variables: { mediaIds: [mediaId], slateCode } });
    }
  };

  const disableSlateButton =
    disabled ||
    liftSlateStatusLoading ||
    setSlateStatusLoading ||
    !selectedSlate ||
    (!!activeSlateCode && !slates?.some((slate) => slate.code === activeSlateCode)) ||
    getIsInAdBreak(adBreakStartedDateTime, adBreakDurationInSeconds);

  const toolTipTitle =
    activeSlateCode || disableSlateButton
      ? 'Remove the active slate to select a different slate.'
      : undefined;

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleTooltipOpen = (nextOpen: boolean) => {
    setTooltipOpen(!!toolTipTitle && nextOpen);
  };

  return (
    <div
      className="flex flex-col items-center gap-2 p-1"
      data-testid={`pipeline-slate-controls-${id}-${mediaId}`}>
      {debugMode ? (
        <>
          <p className="mb-1 rounded-sm border border-dashed p-1 text-sm">
            Active Slate: {activeSlateCode || ''}
          </p>
          <p className="mb-1 rounded-sm border border-dashed p-1 text-sm">
            Selected Slate: {selectedSlate?.code || ''}
          </p>
        </>
      ) : null}

      {slates ? (
        <Tooltip open={!!toolTipTitle && tooltipOpen} onOpenChange={handleTooltipOpen}>
          <TooltipTrigger asChild>
            <ToggleGroup
              className="flex gap-1"
              defaultChecked={true}
              defaultValue={selectedSlate?.code ?? ''}
              disabled={disabled || disableSlateButton || !!activeSlateCode}
              type="single"
              value={selectedSlate?.code ?? ''}
              onValueChange={handleClickSelectSlate}>
              {slates.map((slate: Slate) => {
                return (
                  <ToggleGroupItem
                    key={`${slate.code}`}
                    className="flex h-full w-42.5 flex-col items-center justify-center rounded-md border p-1"
                    value={slate.code}>
                    <figure>
                      <figcaption className="p-1 text-center text-xs">{slate.code}</figcaption>
                      <img alt={slate.type} src={slate.thumbnailUri ?? ''} />
                    </figure>
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </TooltipTrigger>
          <TooltipContent className="text-xs" side="top">
            <p>{toolTipTitle}</p>
          </TooltipContent>
        </Tooltip>
      ) : null}

      <ConfirmationDialog
        description={`Please confirm you want to ${slateIsActive ? 'remove' : 'insert'} the slate.`}
        title={`Confirm ${slateIsActive ? 'Remove' : 'Insert'} Slate`}
        onConfirm={handleClickConfirmUpdate}>
        <Button className="w-full p-6 text-lg" disabled={disableSlateButton}>
          {slateIsActive ? `Remove Slate` : `Insert Slate`}
        </Button>
      </ConfirmationDialog>
    </div>
  );
}
